import React, { useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Avatar from '@mui/material/Avatar';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, GraduationCap, MessageSquare } from 'lucide-react';

const MessageList = ({ messages }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const listRef = useRef(null);
  const isDark = theme?.mode === 'dark';

  const primaryColor = theme?.colors?.primary_light || '#2563eb';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const borderCol = isDark ? 'rgba(51, 65, 85, 0.4)' : '#e2e8f0';

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  if (!messages || messages.length === 0) {
    const accentColor = theme?.colors?.button_light || '#fdbf00';

    return (
      <div 
        className="flex-1 flex flex-col items-center justify-center p-6 text-center transition-colors duration-300"
        style={{ backgroundColor: isDark ? '#0f172a' : '#f8fafc' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="max-w-md p-8 rounded-2xl border shadow-sm flex flex-col items-center space-y-4"
          style={{
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderColor: borderCol
          }}
        >
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mb-2 animate-float-icon"
            style={{ 
              backgroundColor: `${accentColor}12`,
              border: `1.5px solid ${accentColor}25`
            }}
          >
            <MessageSquare size={30} style={{ color: accentColor }} />
          </div>
          
          <h4 className="text-2xl font-bold m-0" style={{ color: isDark ? '#f8fafc' : '#1e293b' }}>
            Sin mensajes previos
          </h4>
          
          <p className="text-xl leading-relaxed m-0" style={{ color: textSecondary }}>
            Escribe un mensaje para iniciar la conversación.
          </p>
        </motion.div>
      </div>
    );
  }

  // Helper to render premium initials avatar if image is missing or default
  const renderMessageAvatar = (fromUser, size = 32) => {
    const hasPhoto = fromUser?.profileimageurl && 
                     !fromUser.profileimageurl.includes('f1.png') && 
                     !fromUser.profileimageurl.includes('f1.svg') && 
                     !fromUser.profileimageurl.includes('f2.png') && 
                     !fromUser.profileimageurl.includes('f2.svg') && 
                     !fromUser.profileimageurl.includes('default');

    const accentColor = theme?.colors?.button_light || '#fdbf00';

    if (!hasPhoto) {
      const initials = fromUser?.fullname 
        ? fromUser.fullname.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() 
        : '?';
      
      const gradient = fromUser?.istutor
        ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}dd 100%)`
        : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`;

      return (
        <Avatar
          style={{
            width: size,
            height: size,
            background: gradient,
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: 'bold',
            border: `1px solid ${borderCol}`
          }}
        >
          {initials}
        </Avatar>
      );
    }

    return (
      <Avatar 
        src={fromUser.profileimageurl} 
        alt={fromUser.fullname}
        style={{ 
          width: size, 
          height: size,
          border: `1px solid ${borderCol}`
        }}
      />
    );
  };

  // Pre-process messages to group by day and detect sender switches
  const renderMessageItems = () => {
    const items = [];
    let lastDateStr = '';
    let lastSenderId = null;

    messages.forEach((msg, idx) => {
      const msgDate = new Date(msg.timecreated * 1000);
      const dateStr = msgDate.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
      
      let displayDate = dateStr;
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      
      if (msgDate.toDateString() === today.toDateString()) {
        displayDate = 'Hoy';
      } else if (msgDate.toDateString() === yesterday.toDateString()) {
        displayDate = 'Ayer';
      }

      // Add a date header if the day changed
      if (displayDate !== lastDateStr) {
        items.push({
          type: 'date-header',
          key: `date-${msg.id || idx}`,
          text: displayDate
        });
        lastDateStr = displayDate;
        lastSenderId = null; // reset sender group on day change
      }

      const isOwn = Number(msg.from?.id) === Number(user.id);
      
      // We show the header (avatar, name) if the sender changed
      const showHeader = msg.from?.id !== lastSenderId;
      lastSenderId = msg.from?.id;

      items.push({
        type: 'message-bubble',
        key: msg.id || idx,
        msg,
        isOwn,
        showHeader,
        timeStr: msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      });
    });

    return items;
  };

  const processedItems = renderMessageItems();

  return (
    <div 
      ref={listRef} 
      className="flex-1 overflow-y-auto p-4 space-y-4 flex flex-col custom-scrollbar transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#0f172a' : '#f8fafc' }}
    >
      <AnimatePresence initial={false}>
        {processedItems.map((item) => {
          if (item.type === 'date-header') {
            return (
              <div 
                key={item.key} 
                className="flex justify-center my-4 animate-fade-in"
              >
                <div 
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-base font-bold shadow-sm border uppercase tracking-wider"
                  style={{ 
                    backgroundColor: isDark ? '#1e293b' : '#ffffff', 
                    color: textSecondary,
                    borderColor: borderCol
                  }}
                >
                  <Calendar size={14} />
                  {item.text}
                </div>
              </div>
            );
          }

          const { msg, isOwn, showHeader, timeStr } = item;
          const senderName = isOwn ? 'Tú' : msg.from?.fullname || 'Usuario';
          const isTutor = msg.from?.istutor;

          return (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.2 }}
              className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} w-full`}
            >
              {/* Sender Name & Role Header */}
              {showHeader && !isOwn && (
                <div className="flex items-center gap-2 ml-12 mb-1.5 animate-fade-in">
                  <span className="text-base font-semibold text-slate-500 dark:text-slate-400">
                    {senderName}
                  </span>
                  {isTutor && (
                    <span 
                      className="text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
                      style={{ 
                        backgroundColor: isDark ? 'rgba(253, 191, 0, 0.15)' : '#fef3c7', 
                        color: isDark ? '#fdbf00' : '#d97706' 
                      }}
                    >
                      <GraduationCap size={12} />
                      Docente
                    </span>
                  )}
                </div>
              )}

              {/* Bubble Layout with optional Avatar */}
              <div className="flex items-end gap-3 max-w-[85%]">
                {/* Received message avatar */}
                {!isOwn && (
                  <div className="w-9 flex-shrink-0">
                    {showHeader ? (
                      renderMessageAvatar(msg.from, 32)
                    ) : (
                      <div style={{ opacity: 0, pointerEvents: 'none' }}>
                        {renderMessageAvatar(msg.from, 32)}
                      </div>
                    )}
                  </div>
                )}

                {/* Actual Bubble */}
                <div className="flex flex-col group">
                  <div
                    className={`p-3.5 text-xl leading-relaxed shadow-sm transition-all duration-300 relative ${
                      isOwn
                        ? 'text-white rounded-2xl rounded-br-sm'
                        : 'rounded-2xl rounded-tl-sm border'
                    }`}
                    style={{
                      backgroundColor: isOwn ? primaryColor : (isDark ? '#1e293b' : '#ffffff'),
                      color: isOwn ? '#ffffff' : (isDark ? '#e2e8f0' : '#1e293b'),
                      borderColor: isOwn ? 'transparent' : borderCol
                    }}
                  >
                    {/* Text content */}
                    <div 
                      dangerouslySetInnerHTML={{ __html: msg.text }}
                      className={`whitespace-pre-wrap break-words [&_p]:m-0 [&_p:not(:last-child)]:mb-2 [&_a]:underline ${
                        isOwn 
                          ? '[&_a]:text-sky-200 hover:[&_a]:text-sky-100' 
                          : '[&_a]:text-blue-600 dark:[&_a]:text-sky-400 hover:[&_a]:text-blue-500'
                      }`}
                    />

                    {/* Inline Timestamp */}
                    <div 
                      className={`text-xs mt-2 text-right font-medium opacity-65 ${
                        isOwn ? 'text-white' : 'text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {timeStr}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default MessageList;
